/*
 * The GroundsMen's lawn auto quoter: the same prices as thegroundsmen.com's instant quote. Copied from the WordPress
 * Steward's steward_lawn_quote (includes/quotelive.php), which the website asks for every price (web-quote, kind lawn), and
 * assets/lawn-pricing.js. Change a number here and there together, or the office and the website quote apart.
 *
 * - Mowing (with edging, trimming and blowing) is priced every 2 weeks by the grass's square feet in tiers, $5 more per
 *   500 sq ft past the last tier, rounded to $5. Weekly is $10 less, a one time cut 1.5 times, each with a lowest price.
 * - Tall grass is 1.6 times the every 2 weeks price, at least $100: the whole job on a one time cut, the first visit on a
 *   schedule. Overgrown grass is priced from photos (no one time cut price at all).
 * - Bagging is a quarter of the mowing price rounded up to $5, at least $15, every visit on a schedule. A hedge trim is
 *   priced from its time at $100 an hour, rounded up to $5. Weed control is $50 a treatment. Both are on the first visit.
 * - Over 1 acre the team quotes by hand.
 */
import { z } from "zod";

export const LAWN_OFTEN = ["weekly", "biweekly", "onetime"] as const;
export type LawnOften = (typeof LAWN_OFTEN)[number];
export const LAWN_CONDITIONS = ["normal", "tall", "overgrown"] as const;
export type LawnCondition = (typeof LAWN_CONDITIONS)[number];
export const LAWN_HEDGES = ["few", "some", "many", "big"] as const;
export type LawnHedges = (typeof LAWN_HEDGES)[number];

/** The lawn price list in whole dollars, the numbers in steward_lawn_pricing. */
export const LAWN_PRICES = {
  tiers: [
    [1200, 50],
    [1950, 55],
    [2700, 60],
    [6000, 65],
    [6750, 70],
    [7500, 75],
  ] as const,
  extraStepSqft: 500,
  extraStepPrice: 5,
  hourlyTarget: 100,
  minSqft: 300,
  maxInstantSqft: 43_560,
  often: {
    weekly: { label: "Weekly", multiplier: 1, offset: -10, minimum: 45, recurring: true },
    biweekly: { label: "Every 2 weeks", multiplier: 1, offset: 0, minimum: 50, recurring: true },
    onetime: { label: "One-time cut", multiplier: 1.5, offset: 0, minimum: 75, recurring: false },
  },
  conditions: {
    normal: { label: "Normal (under 6 in.)", multiplier: 1, minimum: 0, photos: false },
    tall: { label: "Tall (6 to 12 in.)", multiplier: 1.6, minimum: 100, photos: false },
    overgrown: { label: "Overgrown (over 12 in.)", multiplier: 1, minimum: 0, photos: true },
  },
  bagging: { label: "Bag clippings (bags left for your trash pickup)", share: 0.25, min: 15 },
  hedges: {
    label: "Hedge and bush trimming",
    minutesPerBush: 5,
    cleanupMinutes: 15,
    options: {
      few: { label: "1 to 5 bushes", bushes: 5 },
      some: { label: "6 to 15 bushes", bushes: 15 },
      many: { label: "16 to 30 bushes", bushes: 30 },
      big: { label: "More than 30, or taller than 6 ft", bushes: null },
    },
  },
  weeds: { label: "Lawn weed control treatment", price: 50 },
} as const;

/** How often, in the office's words. */
export const LAWN_OFTEN_WORDS: Record<LawnOften, string> = {
  weekly: "Every week",
  biweekly: "Every two weeks",
  onetime: "One time",
};
/** The job frequency a lawn quote gets for each answer. */
export const LAWN_OFTEN_FREQUENCY: Record<LawnOften, string> = {
  weekly: "weekly",
  biweekly: "biweekly",
  onetime: "one-time",
};

export const lawnAnswersShape = z
  .object({
    kind: z.literal("lawn"),
    /** Square feet of grass (the website has the client outline it on the map). */
    sqft: z.number().int().min(0).max(10_000_000).default(0),
    often: z.enum(LAWN_OFTEN).default("biweekly"),
    condition: z.enum(LAWN_CONDITIONS).default("normal"),
    bagging: z.boolean().default(false),
    hedges: z.enum(LAWN_HEDGES).nullable().default(null),
    weeds: z.boolean().default(false),
  })
  .strict();
export type LawnAnswers = z.infer<typeof lawnAnswersShape>;

export interface LawnLine {
  label: string;
  /** Whole cents, or null for one the team prices from photos. */
  cents: number | null;
  /** visit: every mowing visit. once: the first visit. each: each time it is done, the first one on the first visit. */
  per: "visit" | "once" | "each" | "photos";
}

export type LawnPrice =
  | { ok: false; reason: "no-size" | "too-big" | "photos"; sqft: number }
  | {
      ok: true;
      sqft: number;
      recurring: boolean;
      often: LawnOften;
      lines: LawnLine[];
      /** The first visit in all (a one time cut's whole price). */
      firstVisitCents: number;
      /** Every visit after the first, on a schedule. */
      perVisitCents: number | null;
      /** The lines the team prices from photos. */
      photos: string[];
    };

const round5 = (n: number) => Math.round(n / 5) * 5;
const up5 = (n: number) => Math.ceil(n / 5) * 5;

/** Mowing every 2 weeks for this much grass, in dollars, before rounding and minimums. */
export function lawnBase(sqft: number): number {
  const P = LAWN_PRICES;
  const s = Math.max(sqft, P.minSqft);
  for (const [upTo, price] of P.tiers) if (s <= upTo) return price;
  const [lastUpTo, lastPrice] = P.tiers[P.tiers.length - 1]!;
  return lastPrice + Math.ceil((s - lastUpTo) / P.extraStepSqft) * P.extraStepPrice;
}

/** A hedge trim's price in dollars from its time, rounded up to $5; null for one priced from photos. */
export function lawnHedgePrice(key: LawnHedges): number | null {
  const H = LAWN_PRICES.hedges;
  const bushes = H.options[key].bushes;
  if (bushes === null) return null;
  return up5((LAWN_PRICES.hourlyTarget * (H.cleanupMinutes + bushes * H.minutesPerBush)) / 60);
}

/** The lawn price: steward_lawn_quote. */
export function priceLawn(a: LawnAnswers): LawnPrice {
  const P = LAWN_PRICES;
  const sqft = Math.round(Number.isFinite(a.sqft) ? Math.max(0, a.sqft) : 0);
  const f = P.often[a.often];
  const c = P.conditions[a.condition];
  const rec = f.recurring;
  if (!sqft) return { ok: false, reason: "no-size", sqft };
  if (sqft > P.maxInstantSqft) return { ok: false, reason: "too-big", sqft };
  if (!rec && c.photos) return { ok: false, reason: "photos", sqft };
  const mow = Math.max(f.minimum, round5(lawnBase(sqft) * f.multiplier) + f.offset);
  let perVisit = mow;
  const regular = Math.max(P.often.biweekly.minimum, round5(lawnBase(sqft)));
  const tall = c.multiplier > 1 ? Math.max(c.minimum, round5(regular * c.multiplier)) : 0;
  let extra = rec && tall ? Math.max(0, tall - mow) : 0;
  const mowLine = rec ? mow : Math.max(mow, tall);
  const lines: { label: string; amount: number | null; per: LawnLine["per"] }[] = [
    { label: `Mowing, edging and blowing (${f.label.toLowerCase()})`, amount: mowLine, per: rec ? "visit" : "once" },
  ];
  const firstCut = `First cut: ${c.label.toLowerCase()} grass`;
  if (rec && c.photos) lines.push({ label: firstCut, amount: null, per: "photos" });
  else if (rec && extra > 0) lines.push({ label: firstCut, amount: extra, per: "once" });
  if (a.bagging) {
    const amt = Math.max(P.bagging.min, up5(mow * P.bagging.share));
    if (rec) perVisit += amt;
    else extra += amt;
    lines.push({ label: P.bagging.label, amount: amt, per: rec ? "visit" : "once" });
  }
  if (a.hedges) {
    const label = `${P.hedges.label} (${P.hedges.options[a.hedges].label})`;
    const amt = lawnHedgePrice(a.hedges);
    if (amt === null) lines.push({ label, amount: null, per: "photos" });
    else if (amt > 0) {
      extra += amt;
      lines.push({ label, amount: amt, per: "each" });
    }
  }
  if (a.weeds) {
    extra += P.weeds.price;
    lines.push({ label: P.weeds.label, amount: P.weeds.price, per: "each" });
  }
  const out = lines.map((l) => ({ label: l.label, cents: l.amount === null ? null : l.amount * 100, per: l.per }));
  const photos = out.filter((l) => l.cents === null).map((l) => l.label);
  if (!rec) {
    const total = (mowLine + extra) * 100;
    return { ok: true, sqft, recurring: false, often: a.often, lines: out, firstVisitCents: total, perVisitCents: null, photos };
  }
  return {
    ok: true,
    sqft,
    recurring: true,
    often: a.often,
    lines: out,
    firstVisitCents: (perVisit + extra) * 100,
    perVisitCents: perVisit * 100,
    photos,
  };
}

/** The quote lines for a lawn price (steward_lawn_lines): the priced lines, every visit ones repeating, the yard on the first. */
export function lawnQuoteLines(p: LawnPrice): {
  title: string;
  frequency: string;
  lines: { description: string; unitPriceCents: number; recurring: boolean }[];
} | null {
  if (!p.ok) return null;
  const yard = `${p.sqft.toLocaleString("en-US")} sq ft of grass`;
  const priced = p.lines.filter((l): l is LawnLine & { cents: number } => l.cents !== null);
  return {
    title: p.recurring ? `Lawn care ${LAWN_OFTEN_WORDS[p.often].toLowerCase()}` : "One time lawn cut",
    frequency: LAWN_OFTEN_FREQUENCY[p.often],
    lines: priced.map((l, i) => ({
      description: i === 0 ? `${l.label}, ${yard}` : l.label,
      unitPriceCents: l.cents,
      recurring: p.recurring && l.per === "visit",
    })),
  };
}
